import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { isEmpty } from 'lodash'
import { Redirect } from 'react-router-dom'
import { Icon as DownloadIcon } from 'images/download.svg'
import { ConfirmModal, FullPageSpinner } from 'components'
import { displayInteger } from 'utils'

const propTypes = {
  participantsAgreementHash: PropTypes.object,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant),
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  isSubmittingParticipantsData: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  uploadFilePath: PropTypes.string.isRequired,
  summaryData: PropTypes.arrayOf(PropTypes.object).isRequired,
  SummaryTableComponent: PropTypes.elementType.isRequired,
  FlowActionsComponent: PropTypes.elementType.isRequired,
  summaryTableProps: PropTypes.object,
  discardedParticipantsText: PropTypes.string.isRequired,
  heading: PropTypes.string,
}

const defaultProps = {
  participantsAgreementHash: null,
  summaryTableProps: {},
  heading: 'Lastly, review your submission',
}

function ReviewAndSubmitView({
  participantsAgreementHash,
  participantsForSubmission,
  discardedParticipants,
  isSubmittingParticipantsData,
  onSubmit,
  onDownload,
  uploadFilePath,
  summaryData,
  SummaryTableComponent,
  FlowActionsComponent,
  summaryTableProps,
  discardedParticipantsText,
  heading,
}) {
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const handleSubmit = async ({ hasConfirmed = false } = {}) => {
    if (!hasConfirmed && !isEmpty(discardedParticipants) && !hasDownloaded) {
      setShowModal(true)
      return
    }
    await onSubmit()
  }

  const handleDownload = () => {
    setHasDownloaded(true)
    onDownload()
  }

  if (isEmpty(participantsAgreementHash)) {
    return <Redirect to={uploadFilePath} />
  }

  if (isEmpty(participantsForSubmission)) {
    return <Redirect to={uploadFilePath} />
  }

  return (
    <div className="card full-height upload-participant-file-view-container">
      {isSubmittingParticipantsData && <FullPageSpinner showBackground />}
      <div className="form-masthead">
        <h2>{heading}</h2>
      </div>
      <div className="form-section">
        <p>
          Review your submission and verify it contains all your intended plans
          and participants. If you removed any participants during the process,
          they won&apos;t be reflected in the table.
        </p>
        <p>
          We will review your submission, verify participant details, open
          accounts, and email you with account funding instructions.
        </p>
      </div>
      {!isEmpty(discardedParticipants) && (
        <div className="form-section discarded-participants">
          <div className="form-header">
            <h3 className="is-marginless">
              Participants not included in submission
            </h3>
          </div>
          <p>{discardedParticipantsText}</p>
          <div className="download-container">
            <span>
              Total discarded participants:{' '}
              {displayInteger(discardedParticipants.length)}
            </span>
            <button type="button" onClick={handleDownload}>
              <DownloadIcon aria-hidden="true" />
              Download participant data
            </button>
          </div>
        </div>
      )}
      <div className="form-section">
        <SummaryTableComponent
          data={summaryData}
          title={<h3>Submission summary</h3>}
          {...summaryTableProps}
        />
      </div>
      <FlowActionsComponent
        submitContent="Submit"
        handleSubmit={handleSubmit}
      />
      {showModal && (
        <ConfirmModal
          onClose={() => setShowModal(false)}
          onConfirm={() => {
            setShowModal(false)
            handleSubmit({ hasConfirmed: true })
          }}
          confirmContent="Yes, Submit"
        >
          <h2>Download discarded participants</h2>
          <p>
            Are you sure you want to submit without downloading the discarded
            participants?
          </p>
        </ConfirmModal>
      )}
    </div>
  )
}

ReviewAndSubmitView.propTypes = propTypes
ReviewAndSubmitView.defaultProps = defaultProps

export default ReviewAndSubmitView
